import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import { getImageUrlsArray } from "@b2bportal/core-utilities";
import type { DateRange, Lodging } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  LinkToHotel,
  ImageCarousel,
  Rating,
  Location,
  Features,
  TripAdvisorRating,
  HomeFeatures,
  useHomeFeaturesFromHotel,
} from "@tenant/hotels";
import { HotelCardPrice } from "../HotelCardPrice/HotelCardPrice";
import defaultStyles from "@/overrides/hotels/availability/components/HotelMapCard/HotelMapCard.module.scss";

export interface PackageHotelMapCardComponentProps {
  hotel: Lodging;
  dateRange: DateRange;
  rooms?: number;
}

const COMPONENT_KEY = CoreHotelsComponent.HotelMapCard;

export const PackageHotelMapCard = ({
  hotel,
  dateRange,
  rooms = 1,
}: PackageHotelMapCardComponentProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();

  const { isHomes, maxOccupancy, numberOfBedrooms, numberOfBathrooms } =
    useHomeFeaturesFromHotel(hotel);

  return (
    <div className={block}>
      <LinkToHotel hotel={hotel}>
        <ImageCarousel
          className={cn("image-carousel")}
          imageUrlsArray={getImageUrlsArray(hotel.lodging.media)}
        />
        <div className={cn("data-container")}>
          <div className={cn("pre-title-container")}>
            <Rating className={cn("rating")} hotel={hotel} />
            <Location className={cn("location")} city={hotel.lodging.city} />
            {isHomes ? (
              <span className={cn("entire-home")}>
                {t("core-hotels.homes.entireHome")}
              </span>
            ) : (
              <TripAdvisorRating
                className={cn("tripadvisor-rating")}
                hotel={hotel}
              />
            )}
          </div>
          <h2 className={cn("title")}>{hotel.lodging.name}</h2>
          <Features hotel={hotel} />
          {isHomes && (
            <HomeFeatures
              maxOccupancy={maxOccupancy}
              numberOfBedrooms={numberOfBedrooms}
              numberOfBathrooms={numberOfBathrooms}
              className={cn("homes-features")}
            />
          )}
          <HotelCardPrice hotel={hotel} dateRange={dateRange} rooms={rooms} />
        </div>
      </LinkToHotel>
    </div>
  );
};
